import React, { useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MdKeyboardArrowDown } from 'react-icons/md';
import type { MenuItem } from './types';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

interface SidebarMenuItemProps {
  item: MenuItem;
  isCollapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  isCollapsed,
  isOpen,
  onToggle,
}) => {
  const { navigate } = useRestaurantNav();
  const location = useLocation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const hasSubmenu = item.submenu && item.submenu.length > 0;

  // Get restaurant ID from params or localStorage
  const currentRestaurantId = useMemo(() => {
    return restaurantId || localStorage.getItem('restaurantId') || '4bebbb8a-a2bf-4d53-b287-842c3c5be847';
  }, [restaurantId]);

  // Route mapping with restaurant ID
  const getRoute = (id: string): string => {
    const routeMap: Record<string, string> = {
      'dashboard': `/${currentRestaurantId}/dashboard`,
      'Campaign Ideas': `/${currentRestaurantId}/campaign-ideas`,
      'Marketing Calendar': `/${currentRestaurantId}/marketing-calendar`,
      'Demographics': `/${currentRestaurantId}/demographics`,
      'Menu Enhancement': `/${currentRestaurantId}/menu-enhancement`,
      'Review Analysis': `/${currentRestaurantId}/review-analysis`,
      'Competitor': `/${currentRestaurantId}/competitors`,
      'Competitors Promotion': `/${currentRestaurantId}/competitor-promotions`,
      'Integrations': `/${currentRestaurantId}/integrations`,
    };
    return routeMap[id] || `/${currentRestaurantId}/dashboard`;
  };

  const handleClick = () => {
    if (hasSubmenu) {
      onToggle();
    } else {
      navigate(getRoute(item.id));
    }
  };

  const handleSubmenuClick = (subItemId: string) => {
    navigate(getRoute(subItemId));
  };

  // Check if menu item is active
  const isActive = !hasSubmenu && location.pathname === getRoute(item.id);
  const isSubmenuActive = hasSubmenu && item.submenu?.some(sub => location.pathname === getRoute(sub.id));

  return (
    <div>
      {/* Main Menu Item */}
      <motion.button
        onClick={handleClick}
        whileHover={{ backgroundColor: 'rgba(97, 97, 97, 0.2)' }}
        whileTap={{ scale: 0.98 }}
        className="w-full flex items-center cursor-pointer justify-between px-3 py-3 rounded-xl transition-colors relative group"
        style={{
          color: (isActive || isSubmenuActive) ? '#EE4423' : '#ffffff'
        }}
      >
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="flex-shrink-0">
            {item.icon}
          </div>
          <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="text-sm font-normal whitespace-nowrap"
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* Arrow indicator for submenu */}
        {hasSubmenu && !isCollapsed && (
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <MdKeyboardArrowDown size={20} className="text-[#616161]" />
          </motion.div>
        )}

        {/* Tooltip for collapsed state */}
        {/* {isCollapsed && (
          <div className="absolute left-full ml-2 px-3 py-2 bg-[#212121] text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 border border-[#616161]/20">
            {item.label}
            {hasSubmenu && item.submenu && (
              <div className="mt-2 space-y-1 pt-2 border-t border-[#616161]/20">
                {item.submenu.map((sub) => (
                  <div key={sub.id} className="text-[#616161] text-xs">
                    {sub.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )} */}
      </motion.button>

      {/* Submenu */}
      {hasSubmenu && !isCollapsed && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              className="overflow-hidden ml-4 mt-1"
            >
              <div className="relative pl-6 space-y-1">
                {/* Vertical connecting line */}
                <div className="absolute left-3 top-0 bottom-0 w-px bg-[#616161]/30" />

                {item.submenu!.map((subItem, index) => {
                  const isSubActive = location.pathname === getRoute(subItem.id);
                  return (
                    <motion.button
                      key={subItem.id}
                      onClick={() => handleSubmenuClick(subItem.id)}
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{
                        x: 4,
                        backgroundColor: 'rgba(237, 69, 37, 0.1)',
                      }}
                      className="w-full cursor-pointer text-left px-4 py-2.5 rounded-lg text-sm hover:text-white transition-colors relative"
                      style={{
                        color: isSubActive ? '#EE4423' : '#9E9E9E'
                      }}
                    >
                      {/* Connecting dot */}
                      <div 
                        className="absolute left-[-1px] top-1/2 -translate-y-1/2 -translate-x-[13px] w-1.5 h-1.5 rounded-full"
                        style={{
                          backgroundColor: isSubActive ? '#EE4423' : '#616161'
                        }}
                      />
                      {subItem.label}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default SidebarMenuItem;
